import { FlaskConical, Dna, Microscope } from "lucide-react";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";

const RESEARCH_IMG = "https://images.pexels.com/photos/8442110/pexels-photo-8442110.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const therapies = [
  { icon: Dna, label: "Gentherapien", desc: "Direkte Korrektur des MECP2-Gens" },
  { icon: FlaskConical, label: "RNA-basierte Therapien", desc: "Neue Ansätze auf molekularer Ebene" },
  { icon: Microscope, label: "Gen-Editing Technologien", desc: "Präzise Bearbeitung des Genoms" },
];

export default function ResearchHope() {
  const [imageRef, imageVisible] = useScrollAnimation({ threshold: 0.15 });
  const [contentRef, contentVisible] = useScrollAnimation({ threshold: 0.15 });

  return (
    <section id="forschung" data-testid="research-section" className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Image */}
          <div 
            ref={imageRef}
            className={`relative animate-fade-left ${imageVisible ? 'visible' : ''}`}
          >
            <img
              data-testid="research-image"
              src={RESEARCH_IMG}
              alt="Medizinische Forschung im Labor"
              className="w-full rounded-2xl shadow-lg object-cover"
              style={{ aspectRatio: "4/3" }}
              loading="lazy"
            />
            <div className="absolute -bottom-4 -right-4 bg-white rounded-xl shadow-lg p-4 z-10">
              <p className="font-heading font-bold text-lg" style={{ color: "#b46eb6" }}>Hoffnung</p>
              <p className="text-sm" style={{ color: "#9CA3AF" }}>durch Forschung</p>
            </div>
          </div>

          {/* Content */}
          <div 
            ref={contentRef}
            className={`space-y-6 text-left animate-fade-right ${contentVisible ? 'visible' : ''}`}
          >
            <div>
              <p className="font-accent text-xl md:text-2xl mb-2" style={{ color: "#7DD4B5" }}>
                Es gibt Hoffnung
              </p>
              <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight" style={{ color: "#1F2937" }}>
                Die Forschung macht große Fortschritte
              </h2>
              <div className="section-divider mt-4" />
            </div>

            <p className="text-base md:text-lg leading-relaxed" style={{ color: "#4B5563" }}>
              Heute wissen Wissenschaftler, dass das Rett-Syndrom meist durch Veränderungen im{" "}
              <strong style={{ color: "#b46eb6" }}>MECP2-Gen</strong> verursacht wird. Das bedeutet:
              Zum ersten Mal kann direkt an der <strong>Ursache der Krankheit</strong> gearbeitet werden.
            </p>

            <p className="text-base md:text-lg leading-relaxed" style={{ color: "#4B5563" }}>
              Weltweit werden neue Therapieansätze entwickelt:
            </p>

            <div className="space-y-4">
              {therapies.map((item, idx) => {
                const Icon = item.icon;
                return (
                  <div
                    key={idx}
                    data-testid={`therapy-item-${idx}`}
                    className="flex items-center gap-4 p-4 rounded-xl hover:shadow-sm transition-shadow"
                    style={{ background: "rgba(149, 225, 187, 0.25)" }}
                  >
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: "rgba(180, 110, 182, 0.1)" }}>
                      <Icon className="w-5 h-5" style={{ color: "#b46eb6" }} />
                    </div>
                    <div>
                      <p className="font-heading font-semibold text-sm" style={{ color: "#1F2937" }}>{item.label}</p>
                      <p className="text-sm" style={{ color: "#9CA3AF" }}>{item.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            <p className="text-base md:text-lg leading-relaxed" style={{ color: "#4B5563" }}>
              Mehrere dieser Ansätze befinden sich bereits in <strong>klinischen Studien</strong>.
              Organisationen wie der{" "}
              <a 
                href="https://reverserett.org/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="font-bold underline hover:no-underline"
                style={{ color: "#b46eb6" }}
              >
                Rett Syndrome Research Trust (RSRT)
              </a>{" "}
              treiben diese Forschung weltweit aktiv voran.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
